import { lazy, useState } from 'react'
import type { ReactElement } from 'react'
import {
  RestrictToCapability,
  OverrideFeatureFlagDropdownMenuItem,
  SwitchRoleDropdownMenuItem,
  useActiveAccount,
  GlobalHotkeys,
  JoyfulWebNavbar,
  UserEducationPopover,
} from '@joyful/ui/app'
import { DropdownMenuItem, Keyboard } from '@joyful/ui/core'
import { InstallSidecarExtensionButton } from '@/features/navigation/components/InstallSidecarExtensionButton'
import { useAppNavigationItems } from '@/features/navigation/hooks/useAppNavigationItems'
import { useLandingPagePath } from '@/features/navigation/components/LandingPageLink'
import { env } from '@/env'
import { useSetActiveAccount } from '@/features/auth/hooks/useSetActiveAccount'

const LazyDevToolsSheet = lazy(async () => {
  const mod = await import('@/components/DevToolsSheet')
  return {
    default: mod.DevToolsSheet,
  }
})

type NavbarOverlay = null | { type: 'devtools' }

export function Navbar() {
  const activeAccount = useActiveAccount()
  const navigationItems = useAppNavigationItems()
  const homePath = useLandingPagePath()
  const setActiveAccount = useSetActiveAccount()

  const [activeOverlay, setActiveOverlay] = useState<NavbarOverlay>(null)

  return (
    <>
      <JoyfulWebNavbar
        accountItems={navigationItems.expanded.account}
        activeApp="app"
        environment={
          import.meta.env.MODE === 'development'
            ? 'development'
            : env.VITE_JOYFUL_ENV
        }
        frontendCommitMessage={env.VITE_VERCEL_GIT_COMMIT_MESSAGE ?? null}
        frontendCommitSha={
          env.VITE_VERCEL_GIT_COMMIT_SHA ?? env.VITE_COMMIT_SHA ?? null
        }
        frontendCommitTimestamp={
          env.VITE_VERCEL_GIT_COMMIT_TIMESTAMP ??
          env.VITE_COMMIT_TIMESTAMP ??
          null
        }
        generalItems={navigationItems.expanded.general}
        homePath={homePath}
        isAccountSwitchPending={setActiveAccount.isPending}
        manageTeamHref={activeAccount ? `/${activeAccount.slug}/members` : null}
        primaryItems={navigationItems.nav}
        renderPrimaryItem={(itemKey, item) => {
          if (itemKey === 'app:encounters') {
            return (
              <PracticeEncountersNavEducation>
                {item}
              </PracticeEncountersNavEducation>
            )
          }

          return item
        }}
        rightStartAdornment={
          <RestrictToCapability capability="extension.sidecar.view">
            <InstallSidecarExtensionButton />
          </RestrictToCapability>
        }
        currentUserSubmenuItems={
          <>
            <SwitchRoleDropdownMenuItem />
            <OverrideFeatureFlagDropdownMenuItem />
          </>
        }
        utilityMenuItems={
          <>
            {import.meta.env.DEV ? (
              <DropdownMenuItem
                shouldCloseOnSelect
                endAdornment={<Keyboard>{GlobalHotkeys.devTools}</Keyboard>}
                onSelect={() => {
                  setActiveOverlay({
                    type: 'devtools',
                  })
                }}
              >
                {`Development tools…`}
              </DropdownMenuItem>
            ) : null}
          </>
        }
        onSelectAccount={(account) => {
          setActiveAccount.mutate({ account })
        }}
      />
      {import.meta.env.DEV ? (
        <LazyDevToolsSheet
          open={activeOverlay?.type === 'devtools'}
          onOpenChange={(isOpen) => {
            return setActiveOverlay(isOpen ? { type: 'devtools' } : null)
          }}
        />
      ) : null}
    </>
  )
}

function PracticeEncountersNavEducation({
  children,
}: {
  children: ReactElement
}) {
  return (
    <UserEducationPopover
      description={`Browse claims for your practice from this list.`}
      educationId="practice-encounters-nav"
      isActive
      targetEmphasis="caret"
      title={`See where your claims stand`}
    >
      {children}
    </UserEducationPopover>
  )
}
